import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { FiArrowLeft, FiEdit, FiPlus, FiTrash2, FiClock } from 'react-icons/fi';

const LOG_TYPES = ['Daily Work Log', 'Meeting', 'Call', 'Note'];
const TYPE_COLORS = {
  'Daily Work Log': '#3b82f6',
  'Meeting': '#8b5cf6',
  'Call': '#10b981',
  'Note': '#f59e0b',
};

const EMPTY_FORM = { date: new Date().toISOString().slice(0, 10), type: 'Daily Work Log', title: '', description: '', startTime: '', endTime: '', attendees: '' };

const EmployeeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdminHR = user?.role === 'admin' || user?.role === 'hr';
  const isOwnProfile = user?.employeeId === id;

  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('info');

  // Meeting logs state
  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [filterType, setFilterType] = useState('');

  useEffect(() => {
    const fetchEmployee = async () => {
      try {
        const res = await api.get(`/employees/${id}`);
        setEmployee(res.data);
      } catch {
        toast.error('Failed to load employee');
        navigate('/employees');
      } finally {
        setLoading(false);
      }
    };
    fetchEmployee();
  }, [id, navigate]);

  const fetchLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const params = { employee: id };
      if (filterType) params.type = filterType;
      const res = await api.get('/meeting-logs', { params });
      setLogs(res.data);
    } catch {
      toast.error('Failed to load logs');
    } finally {
      setLogsLoading(false);
    }
  }, [id, filterType]);

  useEffect(() => {
    if (tab === 'logs') fetchLogs();
  }, [tab, fetchLogs]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/meeting-logs', { ...form, employee: id });
      toast.success('Log added');
      setForm(EMPTY_FORM);
      setShowForm(false);
      fetchLogs();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save log');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (logId) => {
    if (!window.confirm('Delete this log?')) return;
    try {
      await api.delete(`/meeting-logs/${logId}`);
      toast.success('Log deleted');
      setLogs(prev => prev.filter(l => l._id !== logId));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (!employee) return null;

  const fields = [
    { label: 'Employee ID', value: employee.employeeId },
    { label: 'Name', value: employee.name },
    { label: 'Email', value: employee.email },
    { label: 'Phone', value: employee.phone },
    { label: 'Department', value: employee.department?.name || 'N/A' },
    { label: 'Designation', value: employee.designation },
    { label: 'Joining Date', value: new Date(employee.joiningDate).toLocaleDateString() },
    { label: 'Salary', value: `₹${employee.salary?.toLocaleString()}` },
    { label: 'Status', value: employee.status },
    { label: 'Reporting Person', value: employee.reportingTo ? `${employee.reportingTo.name} (${employee.reportingTo.employeeId})` : 'N/A' },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>Employee Details</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {isAdminHR && (
            <button className="btn-secondary" onClick={() => navigate('/employees')}><FiArrowLeft /> Back</button>
          )}
          {isAdminHR && (
            <Link to={`/employees/${id}/edit`} className="btn-primary"><FiEdit /> Edit</Link>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="tab-bar" style={{ marginBottom: '1.25rem' }}>
        <button className={`tab-btn ${tab === 'info' ? 'active' : ''}`} onClick={() => setTab('info')}>Info</button>
        <button className={`tab-btn ${tab === 'logs' ? 'active' : ''}`} onClick={() => setTab('logs')}>
          Meeting & Work Logs
        </button>
      </div>

      {/* Info Tab */}
      {tab === 'info' && (
        <div className="detail-card">
          <div className="detail-grid">
            {fields.map(f => (
              <div key={f.label} className="detail-item">
                <label>{f.label}</label>
                <span>{f.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Logs Tab */}
      {tab === 'logs' && (
        <div>
          {/* Toolbar */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem', flexWrap: 'wrap', gap: '0.5rem' }}>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <select value={filterType} onChange={e => setFilterType(e.target.value)} style={{ fontSize: '0.875rem' }}>
                <option value="">All Types</option>
                {LOG_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            {(isAdminHR || isOwnProfile) && (
              <button className="btn-primary" onClick={() => setShowForm(v => !v)}>
                <FiPlus /> {showForm ? 'Cancel' : 'Add Log'}
              </button>
            )}
          </div>

          {/* Add Log Form */}
          {showForm && (
            <div className="form-card" style={{ marginBottom: '1rem' }}>
              <h3 style={{ marginBottom: '0.75rem' }}>New Log</h3>
              <form onSubmit={handleSubmit}>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Date *</label>
                    <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} required />
                  </div>
                  <div className="form-group">
                    <label>Type</label>
                    <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
                      {LOG_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Title *</label>
                    <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Brief subject of the log" required />
                  </div>
                  <div className="form-group">
                    <label>Start Time</label>
                    <input type="time" value={form.startTime} onChange={e => setForm({ ...form, startTime: e.target.value })} />
                  </div>
                  <div className="form-group">
                    <label>End Time</label>
                    <input type="time" value={form.endTime} onChange={e => setForm({ ...form, endTime: e.target.value })} />
                  </div>
                  {(form.type === 'Meeting' || form.type === 'Call') && (
                    <div className="form-group">
                      <label>Attendees</label>
                      <input value={form.attendees} onChange={e => setForm({ ...form, attendees: e.target.value })} placeholder="John, Jane, ..." />
                    </div>
                  )}
                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label>Description</label>
                    <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Details, agenda, outcome..." />
                  </div>
                </div>
                <button type="submit" className="btn-primary" style={{ marginTop: '0.75rem' }} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Log'}
                </button>
              </form>
            </div>
          )}

          {/* Logs List */}
          {logsLoading ? (
            <div className="loading">Loading...</div>
          ) : logs.length === 0 ? (
            <div className="empty-state" style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
              No logs found. Add the first log using the button above.
            </div>
          ) : (
            <div className="logs-list">
              {logs.map(log => (
                <div key={log._id} className="log-entry">
                  <div className="log-entry-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <span className="log-type-badge" style={{ background: TYPE_COLORS[log.type] + '22', color: TYPE_COLORS[log.type] }}>
                        {log.type}
                      </span>
                      <strong className="log-title">{log.title}</strong>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <span className="log-date">{new Date(log.date).toLocaleDateString()}</span>
                      {(isAdminHR || log.createdBy?._id === user?.id) && (
                        <button className="btn-sm btn-danger" onClick={() => handleDelete(log._id)} title="Delete"><FiTrash2 /></button>
                      )}
                    </div>
                  </div>
                  {log.description && <p className="log-description">{log.description}</p>}
                  <div className="log-meta">
                    {(log.startTime || log.endTime) && (
                      <span><FiClock size={12} /> {log.startTime || '--:--'} – {log.endTime || '--:--'}</span>
                    )}
                    {log.duration > 0 && (
                      <span>({log.duration} min)</span>
                    )}
                    {log.attendees && <span>Attendees: {log.attendees}</span>}
                    <span>Added by {log.createdBy?.name || 'Unknown'}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default EmployeeDetail;
